import React, { useState, useEffect } from 'react';
import { adminService, AppSetting } from '../../services/api';
import toast from 'react-hot-toast';
import { Save, RefreshCw, CreditCard, Zap, KeyRound, Power } from 'lucide-react';

interface SettingsMap {
  [key: string]: AppSetting;
}

const FIELD_META: Array<{ key: string; label: string; secret: boolean; gateway: 'chargebee' | 'stripe' | null }> = [
  { key: 'chargebee.site', label: 'Chargebee Site', secret: false, gateway: 'chargebee' },
  { key: 'chargebee.api_key', label: 'Chargebee API Key', secret: true, gateway: 'chargebee' },
  { key: 'stripe.secret_key', label: 'Stripe Secret Key', secret: true, gateway: 'stripe' },
  { key: 'stripe.webhook_secret', label: 'Stripe Webhook Secret', secret: true, gateway: 'stripe' },
  { key: 'stripe.price_pro_monthly', label: 'Pro Monthly Price ID', secret: false, gateway: 'stripe' },
  { key: 'stripe.price_pro_yearly', label: 'Pro Yearly Price ID', secret: false, gateway: 'stripe' },
];

const AdminSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<SettingsMap>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gateway, setGateway] = useState<string>('');

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const res = await adminService.getSettings();
      const map: SettingsMap = {};
      const vals: Record<string, string> = {};
      for (const setting of res.data) {
        map[setting.key] = setting;
        vals[setting.key] = setting.isSecret ? '' : setting.value;
      }
      setSettings(map);
      setValues(vals);
      setGateway(map['payment.gateway']?.value || '');
      setDirty({});
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => {
      const next = { ...prev };
      if (value === (settings[key]?.value ?? '')) {
        delete next[key];
      } else if (value.trim() !== '') {
        next[key] = value;
      }
      return next;
    });
  };

  const handleGatewayChange = (gw: string) => {
    setGateway(gw);
    handleChange('payment.gateway', gw);
  };

  const handleSave = async () => {
    const payload = Object.fromEntries(
      Object.entries(dirty).filter(([, v]) => v.trim() !== '')
    );
    if (Object.keys(payload).length === 0) {
      toast('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await adminService.updateSettings(payload);
      toast.success('Settings saved');
      await fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const paymentsEnabled = dirty['payments.enabled'] !== undefined
    ? dirty['payments.enabled'] === 'true'
    : (settings['payments.enabled']?.value ?? 'true') === 'true';

  const updatedAt = (key: string) => {
    const s = settings[key];
    if (!s?.updatedAt) return 'Using environment default';
    try {
      return `Updated ${new Date(s.updatedAt).toLocaleString()}`;
    } catch {
      return 'Updated';
    }
  };

  if (isLoading) {
    return <div className="text-center p-12 text-secondary-foreground">Loading settings...</div>;
  }

  return (
    <div className="max-w-[760px]">
      <div className="bg-white rounded-card p-6 shadow-soft mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-1 font-display flex items-center gap-2">
          <Power className="w-5 h-5" />
          Payment Status
        </h2>
        <p className="text-sm text-secondary-foreground mb-4">
          Enable or disable all payment processing. When disabled, users cannot purchase subscriptions. Existing Pro subscribers retain full access.
        </p>
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleChange('payments.enabled', 'true')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-card border-2 transition-colors text-sm font-medium ${
              paymentsEnabled
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-zinc-200 text-zinc-500 hover:border-zinc-300'
            }`}
          >
            <div className={`w-2.5 h-2.5 rounded-full ${
              paymentsEnabled ? 'bg-emerald-500' : 'bg-zinc-300'
            }`} />
            Enabled
          </button>
          <button
            onClick={() => handleChange('payments.enabled', 'false')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-card border-2 transition-colors text-sm font-medium ${
              !paymentsEnabled
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-zinc-200 text-zinc-500 hover:border-zinc-300'
            }`}
          >
            <div className={`w-2.5 h-2.5 rounded-full ${
              !paymentsEnabled ? 'bg-red-500' : 'bg-zinc-300'
            }`} />
            Disabled
          </button>
        </div>
        <p className="mt-3 text-xs text-secondary-foreground">{updatedAt('payments.enabled')}</p>
      </div>

      <div className="bg-white rounded-card p-6 shadow-soft mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-1 font-display">Payment Gateway</h2>
        <p className="text-sm text-secondary-foreground mb-4">
          Select which billing provider is enabled. This controls checkout and plans for all users.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => handleGatewayChange('stripe')}
            className={`flex items-center gap-3 p-4 rounded-card border-2 transition-colors text-left ${gateway === 'stripe' ? 'border-brand-500 bg-brand-50' : 'border-zinc-200 hover:border-zinc-300'}`}
          >
            <CreditCard className={`w-5 h-5 ${gateway === 'stripe' ? 'text-brand-600' : 'text-secondary-foreground'}`} />
            <div>
              <div className="font-medium text-foreground">Stripe</div>
              <div className="text-xs text-secondary-foreground">Enabled {gateway === 'stripe' ? 'now' : ''}</div>
            </div>
          </button>
          <button
            onClick={() => handleGatewayChange('chargebee')}
            className={`flex items-center gap-3 p-4 rounded-card border-2 transition-colors text-left ${gateway === 'chargebee' ? 'border-brand-500 bg-brand-50' : 'border-zinc-200 hover:border-zinc-300'}`}
          >
            <Zap className={`w-5 h-5 ${gateway === 'chargebee' ? 'text-brand-600' : 'text-secondary-foreground'}`} />
            <div>
              <div className="font-medium text-foreground">Chargebee</div>
              <div className="text-xs text-secondary-foreground">Enabled {gateway === 'chargebee' ? 'now' : ''}</div>
            </div>
          </button>
        </div>
        <p className="mt-3 text-xs text-secondary-foreground">{updatedAt('payment.gateway')}</p>
      </div>

      {['chargebee', 'stripe'].map((gw) => (
        <div key={gw} className="bg-white rounded-card p-6 shadow-soft mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-1 font-display capitalize">{gw} Configuration</h2>
          <p className="text-sm text-secondary-foreground mb-4">
            {gw === 'chargebee' ? 'Chargebee site and API key.' : 'Stripe secret key, webhook secret and Pro price IDs.'}
          </p>
          <div className="space-y-4">
            {FIELD_META.filter((f) => f.gateway === gw).map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-1.5">
                  {field.secret && <KeyRound className="w-3.5 h-3.5 text-secondary-foreground" />}
                  {field.label}
                </label>
                <input
                  type={field.secret ? 'password' : 'text'}
                  value={values[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={
                    field.secret
                      ? settings[field.key]?.value
                        ? 'Unchanged — type a new value to replace'
                        : 'Not set'
                      : ''
                  }
                  autoComplete="off"
                  className="w-full h-11 px-3 text-sm border border-zinc-200 rounded-btn transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                />
                <p className="mt-1 text-xs text-secondary-foreground">{updatedAt(field.key)}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saving || Object.keys(dirty).length === 0}
          className="h-11 px-6 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button
          onClick={fetchSettings}
          className="h-11 px-5 bg-white border border-zinc-200 rounded-btn text-sm text-foreground hover:bg-subtle inline-flex items-center gap-1.5"
        >
          <RefreshCw className="w-4 h-4" />
          Reset
        </button>
      </div>
    </div>
  );
};

export default AdminSettingsTab;
